import { FC } from "react";
import OrderDetailsToolsProps, {
  OrderDetailsToolsPropsStatus,
} from "./types/order-details-tools";
import orderTranslationsHelper from "../../../../languages/order-translations.helper";
import OrderDetailsEditCargoExternalComponent from "./elements/order-details-edit-cargo-external.component";
import OrderDetailsEditTaxiComponent from "./elements/order-details-edit-taxi.component";
import OrderDetailsManageDriverAssignmentComponent from "./elements/order-details-manage-driver-assignment.component";
import OrderDetailsCompletedRouteComponent from "./elements/order-details-completed-route.component";
import OrderDetailsCopyComponent from "./elements/order-details-copy.component";
import OrderDetailsOrderRouteComponent from "./elements/order-details-order-route.component";
import OrderDetailsHistoryComponent from "./elements/order-details-history.component";
import OrderDetailsCancelComponent from "./elements/order-details-cancel.component";
import OrderDetailsCancelRequestComponent from "./elements/order-details-cancel-request.component";
import OrderDetailsForwardingAddComponent from "./elements/order-details-forwarding-add.component";
import OrderDetailsForwardingCancelComponent from "./elements/order-details-forwarding-cancel.component";
import OrderDetailsForwardingEditComponent from "./elements/order-details-forwarding-edit.component";
import useOrderDetailsForwarding from "../modals/use-order-details-forwarding";
import OrderDetailsGoogleMapsRouteComponent from "./elements/order-details-google-maps-route.component";
import OrderDetailsFilteredOrderComponent from "./elements/order-details-filtered-order.component";
import OrderDetailsAcceptComponent from "./elements/order-details-accept.component";
import OrderDetailsRejectComponent from "./elements/order-details-reject.component";

const OrderDetailsToolsComponent: FC<OrderDetailsToolsProps> = ({
  drivers,
  userPermissions,
  cargoOrderDetails,
  isRouteSequenceSolved,
  cargoCompanyUuid,
  isRouteStarted,
  isCancelOrderFetching,
  isCompletedRoutesFetching,
  isCompletedRouteButtonActive,
  isCancelOrderRequestFetching,
  isOptimizerVisible,
  shouldShowOrderHistory,
  executionStatus,
  onOrderAccept,
  onOrderReject,
  cancelOrder,
  onEditTaxiCorporationSuccess,
  onAddCandidatureSuccess,
  onCopyDetailsButtonClick,
  onEditOrderButtonClick,
  onOrderHistoryButtonClick,
  toggleCompletedMapRoutesActive,
  toggleGoogleMapsRoute,
  onCargoExternalIdChangeSuccess,
  onFilteredOrderButtonClick,
  sendOrderCancellationRequest,
  sendRevertOrderCancellationRequest,
}) => {
  const translations = orderTranslationsHelper.getDetailsTranslations();
  const { tools } = translations;
  const {
    hasAccessToEditCargoExternalId,
    hasAccessToEditTaxi,
    hasAccessToManageDriverAssignment,
    hasAccessToEditOrderRoute,
    hasAccessToEditOrderRouteAsTaxi,
    hasAccessToCancelOrder,
    hasAccessToCancelOrderImmediately,
    hasAccessToCancelOrderAsTaxi,
    hasAccessToSendOrderCancelRequest,
    hasAccessToAddOrderForwarding,
    hasAccessToEditOrderForwarding,
    hasAccessToCancelOrderForwarding,
    hasAccessToExtendedDriverAssignment,
    hasAccessToAccept,
    hasAccessToReject,
  } = userPermissions;

  if (!cargoOrderDetails) return;

  const { transportingOrderUuid, editByTaxiAllowed } = cargoOrderDetails;
  const { forwardingId } = useOrderDetailsForwarding({
    transportingOrderId: transportingOrderUuid,
  });

  const hasAccessToEditOrder =
    hasAccessToEditOrderRoute ||
    (hasAccessToEditOrderRouteAsTaxi && editByTaxiAllowed);

  return (
    <div className="order_details_tools">
      <OrderDetailsEditCargoExternalComponent
        details={cargoOrderDetails}
        tools={tools}
        hasAccess={hasAccessToEditCargoExternalId}
        onSuccess={onCargoExternalIdChangeSuccess}
      />
      <OrderDetailsEditTaxiComponent
        cargoCompanyUuid={cargoCompanyUuid}
        details={cargoOrderDetails}
        drivers={drivers}
        tools={tools}
        hasAccess={hasAccessToEditTaxi}
        onSuccess={onEditTaxiCorporationSuccess}
      />
      <OrderDetailsForwardingAddComponent
        tools={tools}
        details={cargoOrderDetails}
        hasAccess={hasAccessToAddOrderForwarding}
      />
      <OrderDetailsForwardingEditComponent
        tools={tools}
        details={cargoOrderDetails}
        hasAccess={hasAccessToEditOrderForwarding}
      />
      <OrderDetailsManageDriverAssignmentComponent
        details={cargoOrderDetails}
        drivers={drivers}
        tools={tools}
        hasAccess={hasAccessToManageDriverAssignment}
        hasAccessToEdit={hasAccessToEditOrder}
        hasAccessToExtendedDriverAssignment={
          hasAccessToExtendedDriverAssignment
        }
        isOptimizerVisible={isOptimizerVisible}
        onSuccess={onAddCandidatureSuccess}
      />
      <OrderDetailsGoogleMapsRouteComponent
        isRouteSequenceSolved={isRouteSequenceSolved}
        tools={tools}
        onSuccess={toggleGoogleMapsRoute}
      />
      <OrderDetailsFilteredOrderComponent
        tools={tools}
        onSuccess={onFilteredOrderButtonClick}
      />
      <OrderDetailsCompletedRouteComponent
        details={cargoOrderDetails}
        tools={tools}
        isRouteStarted={isRouteStarted}
        isLoading={isCompletedRoutesFetching}
        isActive={isCompletedRouteButtonActive}
        onSuccess={toggleCompletedMapRoutesActive}
      />
      <OrderDetailsCopyComponent
        tools={tools}
        details={cargoOrderDetails}
        onSuccess={onCopyDetailsButtonClick}
      />
      <OrderDetailsOrderRouteComponent
        tools={tools}
        details={cargoOrderDetails}
        onSuccess={onEditOrderButtonClick}
        hasAccess={hasAccessToEditOrder}
      />
      <OrderDetailsHistoryComponent
        tools={tools}
        isActive={shouldShowOrderHistory}
        onSuccess={onOrderHistoryButtonClick}
      />
      <OrderDetailsAcceptComponent
        tools={tools}
        orderId={cargoOrderDetails.cargoOrderId}
        status={executionStatus}
        onOrderAccept={onOrderAccept}
        hasAccess={hasAccessToAccept}
      />
      <OrderDetailsRejectComponent
        tools={tools}
        orderId={cargoOrderDetails.cargoOrderId}
        status={executionStatus}
        onOrderReject={onOrderReject}
        hasAccess={hasAccessToReject}
      />
      <OrderDetailsForwardingCancelComponent
        tools={tools}
        details={cargoOrderDetails}
        forwardingId={forwardingId}
        hasAccess={hasAccessToCancelOrderForwarding}
      />
      <OrderDetailsCancelComponent
        tools={tools}
        details={cargoOrderDetails}
        isLoading={isCancelOrderFetching}
        hasAccess={
          hasAccessToCancelOrder ||
          (hasAccessToCancelOrderAsTaxi &&
            (cargoOrderDetails?.cancelByTaxiAllowed ?? false))
        }
        canCancelImmediately={hasAccessToCancelOrderImmediately}
        onConfirm={cancelOrder}
      />
      <OrderDetailsCancelRequestComponent
        details={cargoOrderDetails}
        tools={tools}
        isLoading={isCancelOrderRequestFetching}
        onConfirm={sendOrderCancellationRequest}
        onConfirmRevert={sendRevertOrderCancellationRequest}
        hasAccess={hasAccessToSendOrderCancelRequest}
      />
    </div>
  );
};

export default OrderDetailsToolsComponent;
